import type { RiskAnalystClassification } from './classifyEarlySignalsIntent'
import { isRefineLastTableAsk, type RiskAnalystListSession } from './riskAnalystSession'
import {
  acceptDimensionSlot,
  categoryForDispatch,
  shouldDispatchClassification,
  supplierNameForDispatch,
} from './riskAnalystThread'
import {
  dispatchEarlySignalsAnswer,
  tryAnswerEarlySignalsFromDb,
  type EarlySignalsDbAnswer,
  type TryAnswerEarlySignalsOptions,
} from './tryAnswerEarlySignalsFromDb'

/**
 * On a keyword miss only: run the closed-catalog tool with validated slots.
 * Does not rewrite the question into a canned sentence, and does not run
 * when confidence is below 0.55.
 */
export async function answerFromLlmClassification(options: {
  intent: string
  supplierName: string | null
  confidence: number
  category?: string | null
  subcategory?: string | null
  dimension?: string | null
  /** Original user text — list filters come from these words, not from model slots. */
  userText?: string
  session?: RiskAnalystListSession | null
  defaultProcurementType?: TryAnswerEarlySignalsOptions['defaultProcurementType']
  companyId?: string | null
  linkSpend?: boolean
}): Promise<EarlySignalsDbAnswer | 'clarify' | null> {
  const {
    intent,
    supplierName,
    confidence,
    category,
    subcategory,
    dimension,
    userText,
    session,
    defaultProcurementType,
    companyId,
    linkSpend,
  } = options
  if (!shouldDispatchClassification(confidence, intent)) return null
  if (intent === 'clarify') return 'clarify'

  const trimmed = userText?.trim() || ''
  const dbOptions: TryAnswerEarlySignalsOptions = {
    session,
    defaultProcurementType,
    companyId,
    linkSpend,
  }

  if (session && trimmed && isRefineLastTableAsk(trimmed)) {
    return tryAnswerEarlySignalsFromDb(trimmed, dbOptions)
  }

  const name = supplierNameForDispatch(trimmed, supplierName)
  const categoryName = categoryForDispatch(trimmed, category, subcategory)
  const dimensionKey = acceptDimensionSlot(dimension)
  const routed = classificationFromIntent(intent, name, categoryName)
  if (!routed) return null

  return dispatchEarlySignalsAnswer(routed, trimmed, {
    ...dbOptions,
    dimensionKey,
  })
}

function classificationFromIntent(
  intent: string,
  supplierName: string | null,
  categoryName: string | null
): RiskAnalystClassification | null {
  switch (intent) {
    case 'next_best_actions':
    case 'questions_to_ask':
      return { kind: 'narrative', tool: intent, supplierName }
    case 'supplier_brands':
    case 'supplier_scorecard':
    case 'supplier_dimension_commentary':
    case 'list_active_mitigations':
    case 'list_alerts':
    case 'list_suppliers':
    case 'list_tracker':
    case 'list_reports':
    case 'supplier_summary':
    case 'count_by_band':
    case 'count_by_region':
    case 'count_total_suppliers':
    case 'count_filtered_suppliers':
    case 'group_suppliers_by':
    case 'mitigations_for_supplier':
    case 'supplier_highest_areas':
    case 'spend_portfolio':
    case 'spend_supplier':
      return { kind: 'tool', tool: intent, supplierName }
    case 'highest_in_category':
    case 'category_next_action':
    case 'spend_category':
      return { kind: 'tool', tool: intent, supplierName: null, categoryName }
    default:
      return null
  }
}
